import React, { useState } from 'react';
import { X } from 'lucide-react';
import { transactionAPI } from '../api/client';
import { CATEGORIES } from '../constants/categories';
import { useAnalytics } from '../hooks/useAnalytics';
import { getCurrencySymbol } from '../utils/currency';
import { useTokens } from '../context/ThemeContext';

const SUPPORTED_CURRENCIES = [
  { code: 'INR', label: 'Indian Rupee (₹)' },
  { code: 'USD', label: 'US Dollar ($)' },
  { code: 'EUR', label: 'Euro (€)' },
  { code: 'GBP', label: 'British Pound (£)' },
  { code: 'JPY', label: 'Japanese Yen (¥)' },
  { code: 'AUD', label: 'Australian Dollar (A$)' },
  { code: 'CAD', label: 'Canadian Dollar (C$)' },
  { code: 'CHF', label: 'Swiss Franc (CHF)' },
  { code: 'SGD', label: 'Singapore Dollar (S$)' },
  { code: 'AED', label: 'UAE Dirham (AED)' },
  { code: 'SAR', label: 'Saudi Riyal (SAR)' },
  { code: 'MYR', label: 'Malaysian Ringgit (MYR)' },
  { code: 'THB', label: 'Thai Baht (THB)' },
  { code: 'IDR', label: 'Indonesian Rupiah (IDR)' },
  { code: 'PHP', label: 'Philippine Peso (₱)' },
  { code: 'KRW', label: 'South Korean Won (₩)' },
  { code: 'CNY', label: 'Chinese Yuan (¥)' },
  { code: 'HKD', label: 'Hong Kong Dollar (HK$)' },
  { code: 'VND', label: 'Vietnamese Dong (₫)' },
  { code: 'TWD', label: 'Taiwan Dollar (NT$)' },
  { code: 'NZD', label: 'New Zealand Dollar (NZ$)' },
  { code: 'MXN', label: 'Mexican Peso (MX$)' },
  { code: 'BRL', label: 'Brazilian Real (R$)' },
  { code: 'ZAR', label: 'South African Rand (R)' },
  { code: 'NOK', label: 'Norwegian Krone (kr)' },
  { code: 'SEK', label: 'Swedish Krona (kr)' },
  { code: 'DKK', label: 'Danish Krone (kr)' },
  { code: 'NGN', label: 'Nigerian Naira (₦)' },
  { code: 'KES', label: 'Kenyan Shilling (KSh)' },
  { code: 'EGP', label: 'Egyptian Pound (E£)' },
  { code: 'PKR', label: 'Pakistani Rupee (₨)' },
  { code: 'BDT', label: 'Bangladeshi Taka (৳)' },
];

function toLocalDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function AddExpense({ onClose, onSuccess, baseCurrency = 'INR', customCategories = [] }) {
  const t = useTokens();
  const { track } = useAnalytics();
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(baseCurrency);
  const [date, setDate] = useState(toLocalDateStr(new Date()));
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (amount && parseFloat(amount) > 0) {
      setIsSubmitting(true);
      try {
        await transactionAPI.create({
          amount: parseFloat(amount),
          currency,
          category,
          description: note.trim() || category,
          transactionDate: new Date(date + 'T12:00:00').toISOString(),
          transactionType: 'debit',
          source: 'manual'
        });
        track('expense_added_manual', { category });
        if (onSuccess) onSuccess();
        onClose();
      } catch (error) {
        console.error('Failed to add expense:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const currencySymbol = getCurrencySymbol(currency);
  const catBtn = { padding: '8px 12px', borderRadius: '8px', fontSize: '13px', border: 'none', cursor: 'pointer', backgroundColor: t.chipBg, color: t.chipText };
  const catBtnActive = { backgroundColor: t.itemSelected, color: t.itemSelectedText };
  const catBtnCustom = { backgroundColor: '#ede9fe', color: '#5b21b6' };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', zIndex: 50, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '16px' }} onClick={handleOverlayClick}>
      <div style={{ backgroundColor: t.surface, borderRadius: '24px 24px 0 0', width: '100%', maxWidth: '448px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px', borderBottom: `1px solid ${t.border}` }}>
          <h2 style={{ fontSize: '18px', fontWeight: '500', color: t.text, margin: 0 }}>Add expense</h2>
          <button onClick={onClose} style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: 'none', backgroundColor: 'transparent', cursor: 'pointer' }}>
            <X size={20} color={t.textSecondary} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Amount + Currency */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '14px', color: t.textSlate, marginBottom: '8px' }}>Amount</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', border: `1px solid ${t.inputBorder}`, borderRadius: '12px', overflow: 'hidden', backgroundColor: t.inputBg }}>
                <span style={{ padding: '12px 8px 12px 16px', color: t.textSecondary, fontSize: '20px', flexShrink: 0, lineHeight: 1 }}>{currencySymbol}</span>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  autoFocus
                  style={{ flex: 1, paddingLeft: '4px', paddingRight: '12px', paddingTop: '12px', paddingBottom: '12px', fontSize: '20px', border: 'none', outline: 'none', minWidth: 0, backgroundColor: 'transparent', color: t.text }}
                />
              </div>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                style={{ padding: '12px', fontSize: '14px', border: `1px solid ${t.inputBorder}`, borderRadius: '12px', backgroundColor: t.inputBg, color: t.text, outline: 'none', cursor: 'pointer', flexShrink: 0 }}
              >
                {SUPPORTED_CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.code}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Date */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '14px', color: t.textSlate, marginBottom: '8px' }}>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={toLocalDateStr(new Date())}
              style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: `1px solid ${t.inputBorder}`, borderRadius: '12px', outline: 'none', boxSizing: 'border-box', backgroundColor: t.inputBg, color: t.text }}
            />
          </div>

          {/* Category */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '14px', color: t.textSlate, marginBottom: '8px' }}>Category</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {CATEGORIES.map((cat) => (
                <button key={cat} type="button" onClick={() => setCategory(cat)} style={{ ...catBtn, ...(category === cat ? catBtnActive : {}) }}>{cat}</button>
              ))}
              {customCategories.map((cat) => (
                <button key={cat} type="button" onClick={() => setCategory(cat)} style={{ ...catBtn, ...catBtnCustom, ...(category === cat ? catBtnActive : {}) }}>{cat}</button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '14px', color: t.textSlate, marginBottom: '8px' }}>
              Note <span style={{ color: t.textMuted }}>(optional)</span>
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What was this for?"
              style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: `1px solid ${t.inputBorder}`, borderRadius: '12px', outline: 'none', boxSizing: 'border-box', backgroundColor: t.inputBg, color: t.text }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', paddingTop: '8px' }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '12px 16px', backgroundColor: t.chipBg, color: t.chipText, borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={!amount || parseFloat(amount) <= 0 || isSubmitting}
              style={{ flex: 1, padding: '12px 16px', backgroundColor: t.textSlate, color: 'white', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500', opacity: (!amount || parseFloat(amount) <= 0 || isSubmitting) ? 0.5 : 1 }}
            >
              {isSubmitting ? 'Adding...' : 'Add expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


export default AddExpense;
